import React, { useEffect, useState } from 'react';
import { Link, NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Menu, X, Globe } from 'lucide-react';

import { useSection, useSectionList } from '../content/siteContentContext';

import SmartLink from './SmartLink';
import UpdateToast from '../pwa/UpdateToast';
import { PLAY_URL } from '../utils/links';

/** The languages the site is written in. */
const LANGUAGES = [
  { code: 'fa', label: 'فارسی', dir: 'rtl' },
  { code: 'en', label: 'English', dir: 'ltr' },
];

function useDocumentDirection() {
  const { i18n } = useTranslation();
  const code = i18n.language?.startsWith('en') ? 'en' : 'fa';
  const dir = LANGUAGES.find((item) => item.code === code)?.dir || 'rtl';

  useEffect(() => {
    // Set on <html>, not on a wrapper div: the scrollbar, the text selection
    // and any browser UI that mirrors all read it from the document element.
    document.documentElement.lang = code;
    document.documentElement.dir = dir;
  }, [code, dir]);

  return { code, dir };
}

function LanguageSwitch() {
  const { i18n } = useTranslation();
  const current = i18n.language?.startsWith('en') ? 'en' : 'fa';
  const next = current === 'fa' ? 'en' : 'fa';
  const label = LANGUAGES.find((item) => item.code === next)?.label;

  return (
    <button
      type="button"
      className="btn btn-ghost btn-sm"
      onClick={() => i18n.changeLanguage(next)}
      // The button shows the language it switches *to*, which is the only
      // reading that works when you cannot read the current one.
      aria-label={`Switch to ${label}`}
    >
      <Globe size={16} aria-hidden="true" />
      <span>{label}</span>
    </button>
  );
}

function Header() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const location = useLocation();

  // A drawer left open across a navigation covers the page it just opened.
  useEffect(() => setOpen(false), [location.pathname]);

  const links = [
    { to: '/', label: t('nav.home', 'خانه') },
    { to: '/magazine', label: t('nav.magazine', 'مجله') },
    { to: '/install', label: t('nav.install', 'نصب') },
  ];

  return (
    <header className="site-header">
      <div className="page site-header-inner">
        <Link to="/" className="brand-mark">
          <img src="/logo.png" alt="" width="30" height="30" />
          <span>Shah Poker</span>
        </Link>

        <nav className="site-nav" aria-label={t('nav.primary', 'ناوبری اصلی')}>
          {links.map((link) => (
            <NavLink key={link.to} to={link.to} end={link.to === '/'}>
              {link.label}
            </NavLink>
          ))}
        </nav>

        <div className="header-actions">
          <LanguageSwitch />
          <a
            href={PLAY_URL}
            className="btn btn-primary btn-sm"
            target="_blank"
            rel="noreferrer noopener"
          >
            {t('playNow', 'بازی')}
          </a>
          <button
            type="button"
            className="btn btn-ghost btn-sm menu-toggle"
            onClick={() => setOpen((value) => !value)}
            aria-expanded={open}
            aria-controls="site-drawer"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
            <span className="sr-only">{t('nav.menu', 'منو')}</span>
          </button>
        </div>
      </div>

      {open && (
        <div className="page">
          <nav id="site-drawer" className="nav-drawer">
            {links.map((link) => (
              <NavLink key={link.to} to={link.to} end={link.to === '/'}>
                {link.label}
              </NavLink>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}

function Footer() {
  const footer = useSection('footer');
  const links = useSectionList('footer', 'links');

  // Grouped by the label the admin typed. An ungrouped link still renders,
  // under a heading of its own, rather than disappearing.
  const groups = links.reduce((acc, link) => {
    const key = link.group || '';
    (acc[key] ||= []).push(link);
    return acc;
  }, {});

  return (
    <footer className="site-footer">
      <div className="page">
        <div className="footer-groups">
          <div className="footer-group">
            <Link to="/" className="brand-mark">
              <img src="/logo.png" alt="" width="30" height="30" />
              <span>Shah Poker</span>
            </Link>
            {footer.tagline && (
              <p className="muted" style={{ marginBlockStart: 'var(--space-3)' }}>
                {footer.tagline}
              </p>
            )}
          </div>

          {Object.entries(groups).map(([group, items]) => (
            <div className="footer-group" key={group || 'links'}>
              {group && <h4>{group}</h4>}
              {items.map((link) => (
                <SmartLink key={`${link.href}-${link.label}`} href={link.href}>
                  {link.label}
                </SmartLink>
              ))}
            </div>
          ))}
        </div>

        {footer.responsibleBody && (
          // Plain text, not markdown. Pulling in the markdown parser for one
          // paragraph put 35 kB gzipped on the critical path of every page,
          // including the game. The pages that genuinely need formatting -
          // legal, install, articles - import it themselves and pay for it
          // where it is used.
          <div className="responsible-note">
            {footer.responsibleTitle && <strong>{footer.responsibleTitle}</strong>}
            <p>{footer.responsibleBody}</p>
          </div>
        )}

        <div className="footer-bottom">
          <span>{footer.copyright}</span>
        </div>
      </div>
    </footer>
  );
}

/** The chrome every page sits inside. */
export default function Layout({ children }) {
  const { t } = useTranslation();
  useDocumentDirection();

  return (
    <>
      <a className="skip-link" href="#main">
        {t('nav.skip', 'رفتن به محتوا')}
      </a>
      <Header />
      <main id="main">{children}</main>
      <Footer />
      <UpdateToast />
    </>
  );
}
